import React, { useEffect, useMemo, useState } from "react";
import cn from "classnames";
import { SupportAgentTicket } from "@src/types/support";
import styles from "./ticket-patch-utilities-container.module.scss";
import { PatchTicketPriorityDropdown } from "@src/components/atoms/patch-ticket-priority-dropdown/patch-ticket-priority-dropdown";
import { PatchTicketStatusDropdown } from "@src/components/atoms/patch-ticket-status-dropdown/patch-ticket-status-dropdown";
import { HorizontalSeparator } from "@src/components/atoms/horizontal-separator/horizontal-separator";
import { PatchTicketAssigneesDropdown } from "@src/components/atoms/patch-ticket-assignee-dropdown/patch-ticket-assignee-dropdown";
import companyIcon from "@src/assets/svg/company-icon-full-gray.svg";
import sourceIcon from "@src/assets/svg/machine-icon-light-blue.svg";
import warningIcon from "@src/assets/svg/warning-icon-outlined.svg";
import userIcon from "@src/assets/svg/user-white.svg";
import messagesBubblesIcon from "@src/assets/svg/messages-bubbles.svg";

import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";
import { useI18N } from "@src/language/language-context";
import { querySourceByIdUseCase } from "@src/cloud-connect/client-configuration/use-cases/query-source-by-id-use-case";
import { getMachineWithSkillsUseCase } from "@src/cloud-connect/client-configuration/use-cases/get-machine-with-skills-use-case";
import { SourceTypes } from "@common/types/server-list-types";

import NodeManagerNavigationButtons from "../node-manager-navigation-buttons/node-manager-navigation-buttons";
import { VerticalSeparator } from "@src/components/atoms/vertical-separator/vertical-separator";
import { useTicketVpnManagerHook } from "./use-ticket-vpn-manager-hook";
import { SupportCancelModals } from "@src/components/molecules/support-cancel-modals/support-cancel-modals";
import { MachinesListRemoteSupportRequestedButton } from "@src/components/molecules/machines-remote-support-requested-button/machines-remote-support-requested-button";
import { MachinesListRemoteSupportActiveButton } from "@src/components/molecules/machines-remote-support-active-button/machines-remote-support-active-button";
import {
  MachinesListSupportSessionsStatusTypes,
  MachinesListSupportSessionsZitadelRoleTypes,
} from "@common/types/machines-list-types";
import { ButtonWithIcon } from "@src/components/atoms/buttons/button-with-icon/button-with-icon";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";
import machinesRemoteSupportIcon from "@src/assets/svg/machines-remote-support.svg";
import { SmallSpinner } from "@src/components/atoms/small-spinner/small-spinner";
import { useSSEUnavailable } from "@src/cloud-connect/sse/use-sse-unavailable";
import { MachineSelectionSection } from "@src/components/molecules/source-selection-section/source-selection-section";

interface TicketPatchUtilitiesContainerProps {
  ticket: SupportAgentTicket;
  onTicketUpdated?: (ticket: SupportAgentTicket) => void;
}

const cssClassName = "ticket-patch-utilities-container";

export const TicketPatchUtilitiesContainer = ({
  ticket,
  onTicketUpdated,
}: TicketPatchUtilitiesContainerProps) => {
  const i18n = useI18N();

  const [machineInformation, setMachineInformation] =
    useState<SourceTypes | null>(null);
  const [resolvedMachineName, setResolvedMachineName] = useState<string | null>(
    null,
  );

  const [hasVncSkill, setHasVncSkill] = useState(false);
  const [hasOpcUaSkill, setHasOpcUaSkill] = useState(false);
  const [isMachineUnavailable, setIsMachineUnavailable] =
    useState<boolean>(false);
  const [isCheckingMachineAvailability, setIsCheckingMachineAvailability] =
    useState<boolean>(true);

  const [localRemainingDuration, setLocalRemainingDuration] = useState<
    number | null
  >(null);
  const [isClosingState, setIsClosingState] = useState<boolean>(false);

  const texts = {
    assigneeLabel: i18n("modules.ticketPatchUtilitiesContainer.assigneeLabel"),
    machineLabel: i18n("modules.ticketPatchUtilitiesContainer.machineLabel"),
    sourcePlaceholder: i18n(
      "modules.ticketPatchUtilitiesContainer.sourcePlaceholder",
    ),
    noSourcesFound: i18n(
      "modules.ticketPatchUtilitiesContainer.noSourcesFound",
    ),
    saveButton: i18n("modules.ticketPatchUtilitiesContainer.saveButton"),
    requestedBy: i18n("modules.ticketPatchUtilitiesContainer.requestedBy"),
    via: i18n("modules.ticketPatchUtilitiesContainer.via"),
    remoteMachineLabel: i18n(
      "modules.ticketPatchUtilitiesContainer.remoteMachineLabel",
    ),
    noCompanyInformation: i18n(
      "modules.ticketPatchUtilitiesContainer.noCompanyInformation",
    ),
    noMachineInformation: i18n(
      "modules.ticketPatchUtilitiesContainer.noMachineInformation",
    ),
    noMachineIdInformation: i18n(
      "modules.ticketPatchUtilitiesContainer.noMachineIdInformation",
    ),
    noMachineIPInformation: i18n(
      "modules.ticketPatchUtilitiesContainer.noMachineIPInformation",
    ),
    noUserInformation: i18n(
      "modules.ticketPatchUtilitiesContainer.noUserInformation",
    ),
    unknown: i18n("modules.ticketPatchUtilitiesContainer.unknown"),
    companyAltText: i18n(
      "modules.ticketPatchUtilitiesContainer.companyAltText",
    ),
    sourceIconAltText: i18n(
      "modules.ticketPatchUtilitiesContainer.sourceIconAltText",
    ),
    closing: i18n("modules.ticketPatchUtilitiesContainer.closing"),
    machineNotAvailable: i18n(
      "modules.ticketPatchUtilitiesContainer.machineNotAvailable",
    ),
    missingMachineSkillTooltip: i18n(
      "modules.machinesListRow.missingMachineSkillTooltip",
    ),
    machineConfigurationWarning: i18n(
      "modules.machinesListRow.machineConfigurationWarning",
    ),
  };

  const {
    machine,
    showSTSupport,
    showOTSupport,
    showSTCancelModal,
    showOTCancelModal,
    showDisconnectSupportModal,
    stRequestClickLoading,
    otRequestClickLoading,
    stActiveClickLoading,
    otActiveClickLoading,
    confirmCancelRemoteAccessRequestLoading,
    displayName,
    texts: supportActionTexts,
    modalTexts,
    disconnectTexts,
    openSTCancelRequest,
    openOTCancelRequest,
    closeOTCancelModal,
    closeSTCancelModal,
    closeDisconnectSupportModal,
    confirmCancelRemoteAccessRequest,
    confirmInterruptRemoteAccessRequest,
    handleRequestButtonClick,
    handleSTActiveButtonClick,
    handleOTActiveButtonClick,
    isActionButtonsDisabled,
    isCurrentUserSessionOwner,
    isCurrentUserSessionViewer,
    isManagerOwner,
    canRequestorCancelActiveInProgress,
    canApproverCancelActiveInProgress,
  } = useTicketVpnManagerHook({ ticket });

  const isPrivateSession = machine?.visibility === "private";
  const isSupportActionsDisabled = useSSEUnavailable("service-support");
  const supportSseUnavailableTooltip = isSupportActionsDisabled
    ? i18n("modules.sse.reconnectingActionsUnavailableTooltip")
    : "";

  const isSessionActiveOrOpen =
    machine.status === MachinesListSupportSessionsStatusTypes.ACCEPTED ||
    machine.status === MachinesListSupportSessionsStatusTypes.OPEN;
  const sseUnavailableTooltip = isActionButtonsDisabled
    ? supportActionTexts.sseUnavailableTooltip
    : "";
  const areVncActionsDisabled = isActionButtonsDisabled || !hasVncSkill;
  const vncActionsDisabledTooltip = !hasVncSkill
    ? texts.missingMachineSkillTooltip
    : sseUnavailableTooltip;
  const isDurationExpired =
    isSessionActiveOrOpen &&
    (localRemainingDuration === 0 || machine?.remaining_duration === null);
  useEffect(() => {
    if (isDurationExpired) {
      setIsClosingState(true);
    }

    if (!isSessionActiveOrOpen) {
      setLocalRemainingDuration(null);
      setIsClosingState(false);
    }
  }, [isDurationExpired, isSessionActiveOrOpen]);

  const OTactionCell = useMemo(() => {
    if (isClosingState) {
      return (
        <MachinesListRemoteSupportRequestedButton
          buttonText={texts.closing}
          onCancel={openOTCancelRequest}
          status={machine?.status as MachinesListSupportSessionsStatusTypes}
          buttonVariant="request-modal-ticket-button"
          disableAllActions={areVncActionsDisabled}
          tooltip={vncActionsDisabledTooltip}
          isClosingState
        />
      );
    }

    if (
      (machine.status === MachinesListSupportSessionsStatusTypes.ACCEPTED ||
        machine.status === MachinesListSupportSessionsStatusTypes.OPEN) &&
      !isClosingState &&
      !isDurationExpired &&
      machine?.remaining_duration
    ) {
      return (
        <MachinesListRemoteSupportActiveButton
          onViewDetails={handleOTActiveButtonClick}
          onCancel={openOTCancelRequest}
          requestedDuration={machine?.remaining_duration}
          setLocalRemainingDuration={setLocalRemainingDuration}
          buttonText={supportActionTexts.active}
          loading={otActiveClickLoading}
          zitadelRole={MachinesListSupportSessionsZitadelRoleTypes.ACCEPT_VPN}
          status={machine?.status}
          buttonVariant="request-modal-ticket-button"
          disableAllActions={areVncActionsDisabled}
          tooltip={vncActionsDisabledTooltip}
          isCurrentUserSessionOwner={isCurrentUserSessionOwner}
          isPrivateSession={isPrivateSession}
          showCancelIcon={canApproverCancelActiveInProgress}
        />
      );
    }

    if (
      machine.status === MachinesListSupportSessionsStatusTypes.IN_PROGRESS ||
      machine.isRequested
    ) {
      if (isManagerOwner || isCurrentUserSessionViewer) {
        return (
          <MachinesListRemoteSupportRequestedButton
            buttonText={supportActionTexts.viewing}
            onCancel={openOTCancelRequest}
            loading={otActiveClickLoading}
            status={machine?.status as MachinesListSupportSessionsStatusTypes}
            buttonVariant="request-modal-ticket-button"
            disableAllActions={areVncActionsDisabled}
            tooltip={
              vncActionsDisabledTooltip || supportActionTexts.viewingTooltip
            }
            showCancelIcon={canApproverCancelActiveInProgress}
          />
        );
      }

      let inProgressButtonText = supportActionTexts.joinSession;
      let inProgressTooltip = supportActionTexts.joinSessionTooltip;
      let canJoinInProgress = true;

      switch (isPrivateSession) {
        case true:
          inProgressButtonText = supportActionTexts.inProgressPrivate;
          inProgressTooltip = supportActionTexts.inProgressPrivateTooltip;
          canJoinInProgress = false;
          break;
      }

      return (
        <MachinesListRemoteSupportRequestedButton
          buttonText={inProgressButtonText}
          onViewDetails={
            canJoinInProgress ? handleOTActiveButtonClick : undefined
          }
          onCancel={openOTCancelRequest}
          loading={otActiveClickLoading}
          status={machine?.status as MachinesListSupportSessionsStatusTypes}
          buttonVariant="request-modal-ticket-button"
          disableAllActions={areVncActionsDisabled}
          tooltip={vncActionsDisabledTooltip || inProgressTooltip}
          allowInProgressViewDetails={canJoinInProgress}
          showJoinSessionIcon={canJoinInProgress}
          showCancelIcon={canApproverCancelActiveInProgress}
        />
      );
    }

    if (
      machine.status === MachinesListSupportSessionsStatusTypes.REQUESTED ||
      machine.isRequested
    ) {
      return (
        <MachinesListRemoteSupportRequestedButton
          onViewDetails={(e) =>
            handleRequestButtonClick(
              e,
              MachinesListSupportSessionsZitadelRoleTypes.ACCEPT_VPN,
            )
          }
          onCancel={openOTCancelRequest}
          buttonText={supportActionTexts.incomingRequested}
          loading={otRequestClickLoading}
          buttonVariant="request-modal-ticket-button"
          disableAllActions={areVncActionsDisabled}
          tooltip={vncActionsDisabledTooltip}
        />
      );
    }
  }, [
    isClosingState,
    isDurationExpired,
    machine.status,
    machine?.remaining_duration,
    machine.isRequested,
    openOTCancelRequest,
    areVncActionsDisabled,
    vncActionsDisabledTooltip,
    handleOTActiveButtonClick,
    supportActionTexts.active,
    supportActionTexts.inProgressPrivate,
    supportActionTexts.joinSession,
    supportActionTexts.inProgressPrivateTooltip,
    supportActionTexts.joinSessionTooltip,
    supportActionTexts.viewing,
    supportActionTexts.viewingTooltip,
    supportActionTexts.sseUnavailableTooltip,
    supportActionTexts.incomingRequested,
    otActiveClickLoading,
    otRequestClickLoading,
    handleRequestButtonClick,
    isCurrentUserSessionViewer,
    isManagerOwner,
    isPrivateSession,
    texts.closing,
    canApproverCancelActiveInProgress,
  ]);

  const STactionCell = useMemo(() => {
    if (isClosingState) {
      return (
        <MachinesListRemoteSupportRequestedButton
          buttonText={texts.closing}
          onCancel={openSTCancelRequest}
          status={machine?.status as MachinesListSupportSessionsStatusTypes}
          buttonVariant="request-modal-ticket-button"
          disableAllActions={areVncActionsDisabled}
          tooltip={vncActionsDisabledTooltip}
          isClosingState
        />
      );
    }

    if (
      (machine.status === MachinesListSupportSessionsStatusTypes.ACCEPTED ||
        machine.status === MachinesListSupportSessionsStatusTypes.OPEN) &&
      !isClosingState &&
      !isDurationExpired &&
      machine?.remaining_duration
    ) {
      return (
        <MachinesListRemoteSupportActiveButton
          onViewDetails={handleSTActiveButtonClick}
          onCancel={openSTCancelRequest}
          requestedDuration={machine?.remaining_duration}
          setLocalRemainingDuration={setLocalRemainingDuration}
          buttonText={supportActionTexts.active}
          loading={stActiveClickLoading}
          zitadelRole={MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN}
          status={machine?.status}
          buttonVariant="request-modal-ticket-button"
          disableAllActions={areVncActionsDisabled}
          tooltip={vncActionsDisabledTooltip}
          isCurrentUserSessionOwner={isCurrentUserSessionOwner}
          isPrivateSession={isPrivateSession}
          showCancelIcon={canRequestorCancelActiveInProgress}
        />
      );
    }

    if (machine.status === MachinesListSupportSessionsStatusTypes.IN_PROGRESS) {
      let inProgressButtonText;
      let inProgressTooltip;

      switch (true) {
        case isCurrentUserSessionOwner:
          inProgressButtonText = supportActionTexts.inProgressSession;
          inProgressTooltip = "";
          break;

        case isCurrentUserSessionViewer:
          inProgressButtonText = supportActionTexts.viewing;
          inProgressTooltip = supportActionTexts.viewingTooltip;
          break;

        case isPrivateSession:
          inProgressButtonText = supportActionTexts.inProgressPrivate;
          inProgressTooltip = supportActionTexts.inProgressPrivateTooltip;
          break;

        default:
          inProgressButtonText = supportActionTexts.joinSession;
          inProgressTooltip = supportActionTexts.joinSessionTooltip;
      }

      const canJoinInProgress =
        !isPrivateSession &&
        !isCurrentUserSessionOwner &&
        !isCurrentUserSessionViewer;

      return (
        <MachinesListRemoteSupportRequestedButton
          buttonText={inProgressButtonText}
          onViewDetails={
            canJoinInProgress ? handleSTActiveButtonClick : undefined
          }
          onCancel={openSTCancelRequest}
          loading={stActiveClickLoading}
          status={machine?.status as MachinesListSupportSessionsStatusTypes}
          buttonVariant="request-modal-ticket-button"
          disableAllActions={areVncActionsDisabled}
          tooltip={vncActionsDisabledTooltip || inProgressTooltip}
          allowInProgressViewDetails={canJoinInProgress}
          showJoinSessionIcon={canJoinInProgress}
          showCancelIcon={canRequestorCancelActiveInProgress}
        />
      );
    }

    if (
      machine?.status === MachinesListSupportSessionsStatusTypes.REQUESTED ||
      machine?.isRequested
    ) {
      return (
        <MachinesListRemoteSupportRequestedButton
          onViewDetails={(e) =>
            handleRequestButtonClick(
              e,
              MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN,
            )
          }
          onCancel={openSTCancelRequest}
          buttonText={supportActionTexts.requested}
          loading={stRequestClickLoading}
          buttonVariant="request-modal-ticket-button"
          disableAllActions={areVncActionsDisabled}
          tooltip={vncActionsDisabledTooltip}
          showCancelIcon={canRequestorCancelActiveInProgress}
        />
      );
    }

    if (!machine.gateway_id?.trim() && hasVncSkill) return null;

    return (
      <div>
        <CustomTooltip content={vncActionsDisabledTooltip} maxWidth="360px">
          <ButtonWithIcon
            fun={(e) => {
              if (!e) return;
              handleRequestButtonClick(
                e,
                MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN,
              );
            }}
            icon={machinesRemoteSupportIcon}
            text={supportActionTexts.request}
            variant={["extraSmall"]}
            disabled={areVncActionsDisabled}
            disabledAppearance={areVncActionsDisabled ? "sse" : undefined}
          />
        </CustomTooltip>
      </div>
    );
  }, [
    isClosingState,
    isDurationExpired,
    machine.status,
    machine?.remaining_duration,
    machine?.isRequested,
    machine.gateway_id,
    supportActionTexts.request,
    supportActionTexts.active,
    supportActionTexts.inProgressSession,
    supportActionTexts.inProgressPrivate,
    supportActionTexts.joinSession,
    supportActionTexts.viewing,
    supportActionTexts.inProgressPrivateTooltip,
    supportActionTexts.joinSessionTooltip,
    supportActionTexts.viewingTooltip,
    supportActionTexts.requested,
    areVncActionsDisabled,
    vncActionsDisabledTooltip,
    isCurrentUserSessionOwner,
    isCurrentUserSessionViewer,
    isPrivateSession,
    openSTCancelRequest,
    handleSTActiveButtonClick,
    stActiveClickLoading,
    openOTCancelRequest,
    stRequestClickLoading,
    handleRequestButtonClick,
    texts.closing,
    canRequestorCancelActiveInProgress,
  ]);

  const ticketMachine = ticket.machine;
  const machineId = ticketMachine?.machine_id;

  useEffect(() => {
    let cancelled = false;

    if (!machineId) {
      setIsMachineUnavailable(false);
      setHasVncSkill(false);
      setHasOpcUaSkill(false);
      setMachineInformation(null);
      setResolvedMachineName(null);
      setIsCheckingMachineAvailability(false);
      return;
    }

    setIsCheckingMachineAvailability(true);
    setIsMachineUnavailable(false);
    setHasVncSkill(false);
    setHasOpcUaSkill(false);
    setMachineInformation(null);
    setResolvedMachineName(null);

    const getMachineInformation = async () => {
      try {
        const machineResult = await getMachineWithSkillsUseCase(machineId);
        if (cancelled) return;

        if (!machineResult.success || !machineResult.data) {
          setIsMachineUnavailable(true);
          return;
        }

        setResolvedMachineName(machineResult.data.name);
        const skills = machineResult.data.skills ?? [];
        const vncSkill = skills.some(
          (skill) => skill.type === "VNC" && skill.enabled,
        );
        const opcUaSkill = skills.find(
          (skill) => skill.type === "OPCUA" && skill.enabled,
        );
        setHasVncSkill(vncSkill);
        setHasOpcUaSkill(Boolean(opcUaSkill));
        setIsMachineUnavailable(false);
        setIsCheckingMachineAvailability(false);

        if (opcUaSkill?.data_source_id) {
          try {
            const sourceResult = await querySourceByIdUseCase(
              opcUaSkill.data_source_id,
              false,
            );
            if (cancelled) return;
            if (sourceResult.success && sourceResult.data) {
              setMachineInformation(sourceResult.data);
            }
          } catch {
            // OPC UA source lookup failure must not mark VNC as unavailable.
          }
        }
      } catch {
        if (!cancelled) setIsMachineUnavailable(true);
      } finally {
        if (!cancelled) setIsCheckingMachineAvailability(false);
      }
    };

    getMachineInformation();

    return () => {
      cancelled = true;
    };
  }, [machineId]);

  return (
    <div className={styles[cssClassName]}>
      <div className={styles[`${cssClassName}__patch-dropdowns-wrapper`]}>
        <PatchTicketPriorityDropdown
          ticket={ticket}
          disabled={isSupportActionsDisabled}
          disabledTooltip={supportSseUnavailableTooltip}
          onTicketUpdated={onTicketUpdated}
        />
        <PatchTicketStatusDropdown
          ticket={ticket}
          disabled={isSupportActionsDisabled}
          disabledTooltip={supportSseUnavailableTooltip}
          onTicketUpdated={onTicketUpdated}
        />
      </div>
      <HorizontalSeparator />
      <div className={styles[`${cssClassName}__assignees-wrapper`]}>
        {/* Add more patch utilities here as needed */}
        <p className={styles[`${cssClassName}__label`]}>
          {texts.assigneeLabel}
        </p>
        <PatchTicketAssigneesDropdown
          ticket={ticket}
          disabled={isSupportActionsDisabled}
          disabledTooltip={supportSseUnavailableTooltip}
          onTicketUpdated={onTicketUpdated}
        />
      </div>
      {!machineId && (
        <MachineSelectionSection
          ticketId={ticket.id}
          ticketMachineId={null}
          callbackForRefresh={onTicketUpdated}
          machineLabel={texts.machineLabel}
          machinePlaceholder={texts.sourcePlaceholder}
          noMachinesFound={texts.noSourcesFound}
          confirmButton={texts.saveButton}
        />
      )}
      <div className={styles[`${cssClassName}__company-name-wrapper`]}>
        <img src={companyIcon} alt={texts.companyAltText} />
        <p className={styles[`${cssClassName}__company-name`]}>
          {ticket.org_name || texts.noCompanyInformation}
        </p>
      </div>
      <div className={styles[`${cssClassName}__machine-info-outer-wrapper`]}>
        <div className={styles[`${cssClassName}__machine-info-wrapper`]}>
          <div
            className={cn(styles[`${cssClassName}__machine-name-wrapper`], {
              [styles[`${cssClassName}__machine-name-wrapper--full-width`]]:
                !ticketMachine,
            })}
          >
            <img src={sourceIcon} alt={texts.sourceIconAltText} />
            <p className={styles[`${cssClassName}__machine-name`]}>
              {ticketMachine?.machine_name ||
                resolvedMachineName ||
                machineInformation?.name ||
                machineId ||
                texts.noMachineInformation}
            </p>
          </div>
          <div className={styles[`${cssClassName}__icons-wrapper`]}>
            {machineId && isCheckingMachineAvailability && (
              <div className={styles[`${cssClassName}__spinner-container`]}>
                <SmallSpinner />
              </div>
            )}
            {machineId &&
            !isCheckingMachineAvailability &&
            !isMachineUnavailable ? (
              <>
                <VerticalSeparator
                  style={{ backgroundColor: "rgba(230, 241, 242, 0.05)" }}
                />
                <NodeManagerNavigationButtons
                  machineInformation={machineInformation || undefined}
                  disabledTooltip={
                    !hasOpcUaSkill
                      ? texts.missingMachineSkillTooltip
                      : !machineInformation
                      ? texts.machineConfigurationWarning
                      : undefined
                  }
                />
              </>
            ) : (
              <div className={styles[`${cssClassName}__height-placeholder`]} />
            )}
          </div>
        </div>
        <div className={styles[`${cssClassName}__machine-info-wrapper`]}>
          <div className={styles[`${cssClassName}__machine-id-wrapper`]}>
            <div className={styles[`${cssClassName}__machine-id-label`]}>
              ID
            </div>
            <div className={styles[`${cssClassName}__machine-id-value`]}>
              {machineId || texts.noMachineIdInformation}
            </div>
          </div>
          <div className={styles[`${cssClassName}__machine-ip-wrapper`]}>
            <div className={styles[`${cssClassName}__machine-ip-label`]}>
              IP
            </div>
            <div className={styles[`${cssClassName}__machine-ip-value`]}>
              {ticketMachine?.source_dsn || texts.noMachineIPInformation}
            </div>
          </div>
        </div>
      </div>
      {!isCheckingMachineAvailability && isMachineUnavailable && (
        <div className={styles[`${cssClassName}__machine-warning-wrapper`]}>
          <img src={warningIcon} alt={texts.machineNotAvailable} />
          <p className={styles[`${cssClassName}__machine-warning-message`]}>
            {texts.machineNotAvailable}
          </p>
        </div>
      )}
      {machineId && isCheckingMachineAvailability && (
        <div className={styles[`${cssClassName}__spinner-block`]}>
          <SmallSpinner />
        </div>
      )}
      {machineId && !isCheckingMachineAvailability && !isMachineUnavailable && (
        <div className={styles[`${cssClassName}__user-info-outer-wrapper`]}>
          <div className={styles[`${cssClassName}__user-name-wrapper`]}>
            <p className={styles[`${cssClassName}__label`]}>
              {texts.remoteMachineLabel}
            </p>
          </div>
          <div className={styles[`${cssClassName}__machine-button-wrapper`]}>
            {showSTSupport && STactionCell}
            {showOTSupport && OTactionCell}
          </div>
        </div>
      )}
      <HorizontalSeparator />
      <div className={styles[`${cssClassName}__user-info-outer-wrapper`]}>
        <p className={styles[`${cssClassName}__label`]}>
          {texts.requestedBy} -{" "}
          <TimezoneDate date={ticket.last_updated_at} showRelative />
        </p>
        <div className={styles[`${cssClassName}__user-info-wrapper`]}>
          <div className={styles[`${cssClassName}__user-name-wrapper`]}>
            <img src={userIcon} alt={texts.requestedBy} />
            <p className={styles[`${cssClassName}__user-name`]}>
              {ticket.raised_by?.name || texts.noUserInformation}
            </p>
          </div>
          {ticket.via && (
            <div className={styles[`${cssClassName}__via-wrapper`]}>
              <p className={styles[`${cssClassName}__via-text`]}>
                {texts.via} {ticket.via?.channel || texts.unknown}
              </p>
              <img src={messagesBubblesIcon} alt={texts.via} />
            </div>
          )}
        </div>
      </div>

      {machineId && (
        <SupportCancelModals
          showOTCancelModal={showOTCancelModal}
          showSTCancelModal={showSTCancelModal}
          showDisconnectSupportModal={showDisconnectSupportModal}
          onConfirmOT={confirmInterruptRemoteAccessRequest}
          onConfirmST={confirmCancelRemoteAccessRequest}
          onCancelOT={closeOTCancelModal}
          onCancelST={closeSTCancelModal}
          onCancelDisconnect={closeDisconnectSupportModal}
          machineName={machine?.name || machine?.machine_id}
          companyName={ticket?.org_name || ""}
          isConnected={machine?.connection_state === "CONNECTED"}
          modalTexts={modalTexts}
          disconnectTexts={disconnectTexts}
          loading={confirmCancelRemoteAccessRequestLoading}
          displayName={displayName}
          disableConfirmAction={
            areVncActionsDisabled ||
            isCheckingMachineAvailability ||
            isMachineUnavailable
          }
          disabledConfirmTooltip={
            isCheckingMachineAvailability
              ? ""
              : isMachineUnavailable
              ? texts.machineNotAvailable
              : vncActionsDisabledTooltip
          }
        />
      )}
    </div>
  );
};
